import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Trash2, Users, MessageSquare } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import DecimalInput from '../components/DecimalInput';
import OwnerSelect from '../components/OwnerSelect';
import useUserPreference from '../hooks/useUserPreference';
import useViewMode from '../hooks/useViewMode';
import useFieldLayout from '../hooks/useFieldLayout';
import VistaControles from '../components/VistaControles';
import DataTable from '../components/DataTable';

function SmsPrepaymentsSection({ customerId }) {
  const modal = useModal();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ fecha: new Date().toISOString().slice(0, 10), importeEur: '', invoiceId: '', notas: '' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/sms-prepayments?customerId=${customerId}`);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      modal.error('Error cargando bonos SMS');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (customerId) load(); /* eslint-disable-next-line */ }, [customerId]);

  const total = items.reduce((s, p) => s + Number(p.importeEur), 0);

  const add = async (e) => {
    e.preventDefault();
    const importe = Number(form.importeEur);
    if (!Number.isFinite(importe) || importe <= 0) return modal.error('Importe debe ser > 0');
    try {
      await api.post('/sms-prepayments', {
        customerId,
        fecha: form.fecha,
        importeEur: importe,
        invoiceId: form.invoiceId || null,
        notas: form.notas,
      });
      setForm({ fecha: new Date().toISOString().slice(0, 10), importeEur: '', invoiceId: '', notas: '' });
      await load();
    } catch (err) {
      modal.error(err.response?.data?.message || 'Error guardando');
    }
  };

  const remove = (id) => {
    modal.confirm('¿Borrar este bono SMS?', async () => {
      try {
        await api.delete(`/sms-prepayments/${id}`);
        await load();
      } catch (e) {
        modal.error('Error borrando');
      }
    });
  };

  return (
    <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 16, paddingTop: 16 }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 12px' }}>
        <MessageSquare size={18} /> Bonos SMS prepagados
        <span style={{ marginLeft: 'auto', fontWeight: 400, color: '#64748b', fontSize: 14 }}>
          Total prepagado: <strong style={{ color: '#15803d' }}>{total.toFixed(2)} €</strong>
        </span>
      </h3>

      {loading ? (
        <div style={{ color: '#64748b', fontSize: 13 }}>Cargando…</div>
      ) : items.length === 0 ? (
        <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 8 }}>Sin bonos registrados.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
          {items.map((p) => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', background: '#f8fafc', borderRadius: 6, fontSize: 13 }}>
              <span style={{ color: '#64748b', width: 90 }}>{String(p.fecha).slice(0, 10)}</span>
              <strong style={{ width: 70 }}>{Number(p.importeEur).toFixed(2)} €</strong>
              {p.invoiceId && <span style={{ color: '#475569' }}>Fact #{p.invoiceId}</span>}
              <span style={{ flex: 1, color: '#64748b', fontSize: 12 }}>{p.notas}</span>
              <button type="button" className="btn-icon" title="Borrar" style={{ color: '#ef4444' }} onClick={() => remove(p.id)}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '120px 100px 90px 1fr auto', gap: 8, alignItems: 'end' }}>
        <div>
          <label className="form-label" style={{ fontSize: 12 }}>Fecha</label>
          <input type="date" className="form-input" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
        </div>
        <div>
          <label className="form-label" style={{ fontSize: 12 }}>Importe €</label>
          <input type="number" step="0.01" className="form-input" value={form.importeEur} onChange={(e) => setForm({ ...form, importeEur: e.target.value })} placeholder="50.00" />
        </div>
        <div>
          <label className="form-label" style={{ fontSize: 12 }}>Factura id</label>
          <input type="number" className="form-input" value={form.invoiceId} onChange={(e) => setForm({ ...form, invoiceId: e.target.value })} placeholder="opcional" />
        </div>
        <div>
          <label className="form-label" style={{ fontSize: 12 }}>Notas</label>
          <input className="form-input" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} placeholder="ej. Bono SMS 50€" />
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={add}>
          <Plus size={16} /> Añadir
        </button>
      </div>
    </div>
  );
}

const emptyCustomer = {
  name: '', tradeName: '', email: '', phone: '', mobile: '', nif: '',
  address: '', city: '', province: '', postalCode: '', country: 'España',
  notes: '', status: 'activo', discountRate: 0, ownerId: null
};

const Customers = () => {
  const { hasPermission } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useUserPreference('customers:search', '');
  const [viewMode, setViewMode] = useViewMode('customers');
  const fl = useFieldLayout('customers', viewMode);
  const [showModal, setShowModal] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyCustomer });
  const modal = useModal();

  useEffect(() => { loadCustomers(); }, []);

  const loadCustomers = async () => {
    try {
      const res = await api.get('/customers');
      setCustomers(res.data);
    } catch (error) {
      modal.error('Error cargando clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        discountRate: Number(form.discountRate || 0)
      };
      if (editing) {
        const res = await api.put(`/customers/${editing}`, payload);
        setCustomers(customers.map(c => c.id === editing ? res.data : c));
      } else {
        const res = await api.post('/customers', payload);
        setCustomers([res.data, ...customers]);
      }
      closeModal();
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error guardando cliente');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.delete(`/customers/${id}`);
      const updated = await api.get('/customers');
      setCustomers(updated.data);
      const invoiceCount = Number(res.data?.relatedInvoices || 0);
      if (invoiceCount > 0) {
        modal.info(`Cliente archivado. Está relacionado con ${invoiceCount} factura(s).`);
      }
      setCustomerToDelete(null);
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error archivando cliente');
    }
  };

  const openEdit = (customer) => {
    setEditing(customer.id);
    setForm({
      name: customer.name, tradeName: customer.tradeName || '',
      email: customer.email, phone: customer.phone, mobile: customer.mobile || '',
      nif: customer.nif, address: customer.address || '', city: customer.city || '',
      province: customer.province || '', postalCode: customer.postalCode || '',
      country: customer.country || 'España', notes: customer.notes, status: customer.status,
      discountRate: parseFloat(customer.discountRate || 0),
      ownerId: customer.ownerId ?? null
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm({ ...emptyCustomer });
  };

  const filtered = customers.filter(c =>
    (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.nif || '').toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    { key: 'name', header: 'Nombre', className: 'dt-strong', fixed: true, render: (c) => (
      <div>
        <span className="dt-sub">#{c.id}</span> {c.name}
        {c.tradeName && <div className="dt-sub" style={{ fontWeight: 400 }}>{c.tradeName}</div>}
      </div>
    ) },
    { key: 'nif', header: 'NIF', render: (c) => c.nif || '—' },
    { key: 'email', header: 'Email', render: (c) => c.email || '—' },
    { key: 'phone', header: 'Teléfono', render: (c) => c.phone || '—' },
    { key: 'invoices', header: 'Facturas', className: 'num', render: (c) => (
      Number.isInteger(c._count?.invoices) ? c._count.invoices : '—'
    ) },
    { key: 'discountRate', header: 'Dto.', className: 'num', render: (c) => (
      parseFloat(c.discountRate || 0) > 0 ? `${c.discountRate}%` : '—'
    ) },
    { key: 'status', header: 'Estado', render: (c) => (
      <span className={`badge ${c.status === 'activo' ? 'badge-success' : 'badge-gray'}`}>{c.status}</span>
    ) },
    { key: 'actions', header: '', className: 'col-actions', render: (c) => (
      <div className="dt-actions">
        {hasPermission('customers', 'edit') && (
          <button className="btn-icon" onClick={() => openEdit(c)} title="Editar"><Edit2 size={16} /></button>
        )}
        {hasPermission('customers', 'delete') && (
          <button className="btn-icon" onClick={() => setCustomerToDelete(c.id)} title="Archivar" style={{ color: '#ef4444' }}><Trash2 size={16} /></button>
        )}
      </div>
    ) },
  ];

  const fields = fl.fields(columns);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Clientes</h1>
          <p className="page-subtitle">{customers.length} clientes registrados</p>
        </div>
        {hasPermission('customers', 'create') && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={18} /> Nuevo cliente
          </button>
        )}
      </div>

      <div className="card">
        <div className="search-bar">
          <div className="search-input-wrapper">
            <Search />
            <input
              type="text"
              className="form-input"
              placeholder="Buscar por nombre, email o NIF..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <VistaControles
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              fields={fields}
              hidden={fl.hidden}
              onToggleField={fl.toggle}
              onReorderFields={fl.reorder}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <Users size={64} />
            <h3>No hay clientes</h3>
            <p>Añade tu primer cliente para empezar</p>
          </div>
        ) : viewMode === 'table' ? (
          <DataTable columns={fl.orderColumns(columns)} rows={filtered} hiddenKeys={fl.hidden} />
        ) : (
          <div className="items-grid">
            {filtered.map(customer => (
              <div className="item-card" key={customer.id}>
                <div className="item-card-header">
                  <div className="item-card-avatar">{customer.name.charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="item-card-title"><span style={{ color: '#94a3b8', fontWeight: 400, fontSize: '0.8em' }}>#{customer.id}</span> {customer.name}</div>
                    {customer.tradeName && <div className="item-card-subtitle">{customer.tradeName}</div>}
                  </div>
                  {!fl.isHidden('status') && (
                    <span className={`badge ${customer.status === 'activo' ? 'badge-success' : 'badge-gray'}`}>
                      {customer.status}
                    </span>
                  )}
                </div>
                <div className="item-card-body">
                  {!fl.isHidden('nif') && customer.nif && (
                    <div className="item-card-row" style={fl.cardStyle('nif')}>
                      <span className="item-label">NIF</span>
                      <span>{customer.nif}</span>
                    </div>
                  )}
                  {!fl.isHidden('email') && customer.email && (
                    <div className="item-card-row" style={fl.cardStyle('email')}>
                      <span className="item-label">Email</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{customer.email}</span>
                    </div>
                  )}
                  {!fl.isHidden('phone') && customer.phone && (
                    <div className="item-card-row" style={fl.cardStyle('phone')}>
                      <span className="item-label">Tel</span>
                      <span>{customer.phone}</span>
                    </div>
                  )}
                  {!fl.isHidden('invoices') && (
                    <div className="item-card-row" style={fl.cardStyle('invoices')}>
                      <span className="item-label">Facturas</span>
                      <span>{Number.isInteger(customer._count?.invoices) ? customer._count.invoices : '—'}</span>
                    </div>
                  )}
                  {!fl.isHidden('discountRate') && parseFloat(customer.discountRate || 0) > 0 && (
                    <div className="item-card-row" style={fl.cardStyle('discountRate')}>
                      <span className="item-label">Descuento</span>
                      <span>{customer.discountRate}%</span>
                    </div>
                  )}
                </div>
                <div className="item-card-footer">
                  <div className="item-card-actions">
                    {hasPermission('customers', 'edit') && (
                      <button className="btn-icon" onClick={() => openEdit(customer)} title="Editar">
                        <Edit2 size={16} />
                      </button>
                    )}
                    {hasPermission('customers', 'delete') && (
                      <button className="btn-icon" onClick={() => setCustomerToDelete(customer.id)} title="Archivar" style={{ color: '#ef4444' }}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Editar cliente' : 'Nuevo cliente'}</h2>
              <button className="btn-icon" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Nombre *</label>
                    <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">NIF/CIF</label>
                    <input className="form-input" value={form.nif} onChange={e => setForm({ ...form, nif: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Teléfono</label>
                    <input className="form-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Dirección</label>
                  <input className="form-input" placeholder="Calle y número" value={form.address}
                    onChange={e => setForm({ ...form, address: e.target.value })} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Ciudad</label>
                    <input className="form-input" value={form.city}
                      onChange={e => setForm({ ...form, city: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Código postal</label>
                    <input className="form-input" value={form.postalCode}
                      onChange={e => setForm({ ...form, postalCode: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Descuento por defecto (%)</label>
                    <DecimalInput
                      min="0"
                      max="100"
                      step="0.1"
                      value={form.discountRate}
                      onValueChange={value => setForm({ ...form, discountRate: value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Estado</label>
                    <select className="form-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                    </select>
                  </div>
                </div>
                {editing && (
                  <OwnerSelect
                    value={form.ownerId}
                    onChange={(id) => setForm({ ...form, ownerId: id })}
                  />
                )}
                <div className="form-group">
                  <label className="form-label">Notas</label>
                  <textarea className="form-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                </div>
                {editing && <SmsPrepaymentsSection customerId={editing} />}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Guardar cambios' : 'Crear cliente'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {customerToDelete !== null && (
        <div className="modal-overlay" onClick={() => setCustomerToDelete(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmar archivado</h2>
              <button className="btn-icon" onClick={() => setCustomerToDelete(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Este cliente no se borrará físicamente. Se marcará como inactivo para conservar historial.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline" onClick={() => setCustomerToDelete(null)}>Cancelar</button>
              <button type="button" className="btn btn-danger" onClick={() => handleDelete(customerToDelete)}>Archivar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
